import React from 'react';
import { ExternalLink, CheckSquare, Calendar, MapPin, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

const VotingInfo = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-br from-navy-900 to-navy-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold mb-4">Voting Information</h1>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto">
            Everything you need to know to make your voice heard on Election Day
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto space-y-8">
            {/* Check Registration */}
            <Card className="shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <div className="flex items-start space-x-4">
                  <CheckSquare className="w-10 h-10 text-red-600 flex-shrink-0 mt-1" />
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-navy-900 mb-3">Check Your Registration</h2>
                    <p className="text-gray-700 mb-4 leading-relaxed">
                      Make sure you're registered to vote in New York. Check your voter registration status, 
                      update your information, or register to vote if you haven't already.
                    </p>
                    <Button 
                      className="bg-red-600 hover:bg-red-700 text-white font-semibold"
                      onClick={() => window.open('https://voterlookup.elections.ny.gov/', '_blank')}
                    >
                      Check Registration Status <ExternalLink className="ml-2 w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Find Polling Place */}
            <Card className="shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <div className="flex items-start space-x-4">
                  <MapPin className="w-10 h-10 text-red-600 flex-shrink-0 mt-1" />
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-navy-900 mb-3">Find Your Polling Place</h2>
                    <p className="text-gray-700 mb-4 leading-relaxed">
                      Enter your address to find your designated polling location for Election Day. 
                      Polls are typically open from 6:00 AM to 9:00 PM.
                    </p>
                    <Button 
                      className="bg-red-600 hover:bg-red-700 text-white font-semibold"
                      onClick={() => window.open('https://voterlookup.elections.ny.gov/', '_blank')}
                    >
                      Find My Polling Place <ExternalLink className="ml-2 w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Early Voting */}
            <Card className="shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <div className="flex items-start space-x-4">
                  <Calendar className="w-10 h-10 text-red-600 flex-shrink-0 mt-1" />
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-navy-900 mb-3">Early Voting</h2>
                    <p className="text-gray-700 mb-4 leading-relaxed">
                      Can't make it to the polls on Election Day? New York offers early voting at designated 
                      locations. Check dates, times, and locations for early voting in your county.
                    </p>
                    <Button 
                      className="bg-red-600 hover:bg-red-700 text-white font-semibold"
                      onClick={() => window.open('https://www.elections.ny.gov/early-voting.html', '_blank')}
                    >
                      Early Voting Info <ExternalLink className="ml-2 w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Absentee Ballot */}
            <Card className="shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <div className="flex items-start space-x-4">
                  <Mail className="w-10 h-10 text-red-600 flex-shrink-0 mt-1" />
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-navy-900 mb-3">Absentee Ballot</h2>
                    <p className="text-gray-700 mb-4 leading-relaxed">
                      If you'll be away from your county on Election Day, have an illness or disability, or meet 
                      other qualifications, you can vote by absentee ballot. Request yours today.
                    </p>
                    <Button 
                      className="bg-red-600 hover:bg-red-700 text-white font-semibold"
                      onClick={() => window.open('https://absenteeballot.elections.ny.gov/', '_blank')}
                    >
                      Request Absentee Ballot <ExternalLink className="ml-2 w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Important Dates */}
            <Card className="bg-red-50 border-red-200">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-navy-900 mb-4">Important Dates</h2>
                <div className="space-y-3">
                  <div className="flex justify-between items-center py-3 border-b border-red-200">
                    <span className="font-semibold text-navy-900">Voter Registration Deadline</span>
                    <span className="text-gray-700">Check with local Board of Elections</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-red-200">
                    <span className="font-semibold text-navy-900">Early Voting Period</span>
                    <span className="text-gray-700">Dates TBD</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-red-200">
                    <span className="font-semibold text-navy-900">Absentee Ballot Request Deadline</span>
                    <span className="text-gray-700">Check with local Board of Elections</span>
                  </div>
                  <div className="flex justify-between items-center py-3">
                    <span className="font-semibold text-navy-900">Election Day</span>
                    <span className="text-red-600 font-bold">TBD 2025</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Need Help */}
            <div className="text-center bg-navy-900 text-white rounded-lg p-8">
              <h2 className="text-2xl font-bold mb-3">Need Help Voting?</h2>
              <p className="text-gray-200 mb-6">
                Our campaign team is here to assist you with any questions about registration, polling locations, 
                or the voting process.
              </p>
              <Button 
                size="lg" 
                className="bg-red-600 hover:bg-red-700 text-white font-semibold"
                onClick={() => window.location.href = '/#signup-form'}
              >
                Contact Our Team
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default VotingInfo;
